/**
 * Mycelium Protocol Initialization
 *
 * Bootstrap script to wire up all mycelium interconnections.
 * Uses the ConnectorRegistry for dynamic, discoverable connector management.
 *
 * All connectors implement IMyceliumConnector and self-register via metadata.
 * The registry handles transport-aware skipping, ordered init, and reverse disposal.
 *
 * @module mycelium-init
 * @since 2026-02-19
 * @see docs/protocols/mycelium/CONNECTOR_FRAMEWORK.md
 */

import { serviceRegistry } from "./service-registry";
import { myceliumEventBackbone } from "./mycelium-event-backbone.service";
import { activityGamificationConnector } from "./mycelium-activity-gamification.connector";
import { gmailCalendarTasksConnector } from "./mycelium-gmail-calendar-tasks.connector";
import { xeroCommissionGamificationConnector } from "./mycelium-xero-commission-gamification.connector";
import { notificationChannelEscalationConnector } from "./mycelium-notification-escalation.connector";
import { cortexContextEnhancementConnector } from "./mycelium-cortex-context.connector";
import { salesPipelineIntelligenceConnector } from "./mycelium-sales-pipeline-intelligence.connector";
import { documentTaskExtractionConnector } from "./mycelium-document-task-extraction.connector";
import { documentGenerationConnector } from "./mycelium-document-generation.connector";
import { myceliumTimeDependencyConnector } from "./mycelium-time-dependency.connector";
import { auditTrailConnector } from "./mycelium-audit-trail.connector";
import { consoleLogConnector } from "./console";
import { teamGamificationConnector } from "./mycelium-team-gamification.connector";
import { webhookBridgeConnector } from "./mycelium-webhook-bridge.connector";
import { cortexTriggerConnector } from "./mycelium-cortex-trigger.connector";
import { messagingConnector } from "./mycelium-messaging.connector";
import { memoryConsolidatorConnector } from "./mycelium-memory-consolidator.connector";
import { pgmqHealthMonitor } from "./mycelium-pgmq-health-monitor";
import { initializeForms, disposeForms } from "./forms/register";
import { databaseChangeConnector } from "./mycelium-database-change.connector";
import { getMyceliumTransportType } from "./mycelium-transport-registry";
import { connectorRegistry } from "./mycelium-connector-registry";
import type { IMyceliumConnector, ConnectorMetadata } from "./mycelium-connector.interface";
import type { EventDomain } from "./mycelium-event-backbone.service";
import { logger } from "../lib/logger";

// ============================================================================
// CONNECTOR ADAPTERS
// ============================================================================

/**
 * Wraps a legacy connector singleton into an IMyceliumConnector.
 * This adapter bridges existing connectors to the formal interface
 * without requiring changes to each connector's source file.
 */
function adaptConnector(
  legacy: { initialize(): Promise<void>; dispose(): Promise<void>; },
  meta: ConnectorMetadata,
  preInit?: () => void,
): IMyceliumConnector {
  let _initialized = false;
  return {
    metadata: meta,
    get initialized() { return _initialized; },
    async initialize() {
      if (_initialized) return;
      preInit?.();
      await legacy.initialize();
      _initialized = true;
    },
    async dispose() {
      if (!_initialized) return;
      await legacy.dispose();
      _initialized = false;
    },
  };
}

// ============================================================================
// CONNECTOR METADATA DECLARATIONS
// ============================================================================

/**
 * All connector metadata in registration order.
 * Registration order matters: audit trail should be last (observes all),
 * PGMQ-only connectors are auto-skipped based on transport.
 */
const CONNECTOR_DEFINITIONS: { connector: IMyceliumConnector }[] = [
  {
    connector: adaptConnector(activityGamificationConnector, {
      id: "activity-gamification",
      name: "Activity-Gamification",
      description: "Tasks/Sales activities → XP awards + streak tracking",
      phase: 1,
      subscribedDomains: ["task", "sales", "calendar", "accounting"] as EventDomain[],
      publishedDomains: ["gamification"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(gmailCalendarTasksConnector, {
      id: "gmail-calendar-tasks",
      name: "Gmail-Calendar-Tasks Triangle",
      description: "Email → Calendar → Tasks bidirectional sync",
      phase: 2,
      subscribedDomains: ["calendar", "task", "notification"] as EventDomain[],
      publishedDomains: ["task", "calendar"] as EventDomain[],
      requiresPgmq: false,
      executionSurface: "both",
    }),
  },
  {
    connector: adaptConnector(xeroCommissionGamificationConnector, {
      id: "xero-commission-gamification",
      name: "Xero-Commission-Gamification",
      description: "Invoices → commissions → XP + Xero sync",
      phase: 2,
      subscribedDomains: ["accounting", "sales"] as EventDomain[],
      publishedDomains: ["gamification", "accounting"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(notificationChannelEscalationConnector, {
      id: "notification-escalation",
      name: "Notification Channel Escalation",
      description: "Priority-based multi-channel notification routing",
      phase: 2,
      subscribedDomains: ["notification", "task", "sales"] as EventDomain[],
      publishedDomains: ["notification"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(cortexContextEnhancementConnector, {
      id: "cortex-context-enhancement",
      name: "Cortex Context Enhancement",
      description: "AI context enrichment + quick actions filtering",
      phase: 2,
      subscribedDomains: ["user", "system", "ai"] as EventDomain[],
      publishedDomains: ["ai"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(salesPipelineIntelligenceConnector, {
      id: "sales-pipeline-intelligence",
      name: "Sales Pipeline Intelligence",
      description: "Deal velocity + engagement scoring + stall detection",
      phase: 3,
      subscribedDomains: ["sales", "crm"] as EventDomain[],
      publishedDomains: ["sales"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(documentTaskExtractionConnector, {
      id: "document-task-extraction",
      name: "Document-Task Extraction",
      description: "Google Docs → hierarchical task extraction",
      phase: 3,
      subscribedDomains: ["documents"] as EventDomain[],
      publishedDomains: ["task"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(documentGenerationConnector, {
      id: "document-generation",
      name: "Document Generation",
      description: "Proposals → Invoices → Payments → Xero pipeline",
      phase: 3,
      subscribedDomains: ["documents", "sales", "accounting"] as EventDomain[],
      publishedDomains: ["documents", "accounting", "gamification"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(
      myceliumTimeDependencyConnector,
      {
        id: "time-dependency",
        name: "Time-Dependency Tracking",
        description: "Time/workload/estimation/dependency intelligence",
        phase: 4,
        subscribedDomains: ["task"] as EventDomain[],
        publishedDomains: ["task"] as EventDomain[],
        requiresPgmq: false,
      },
      // Pre-init hook: inject backbone reference (legacy pattern)
      () => myceliumTimeDependencyConnector.setEventBackbone(myceliumEventBackbone),
    ),
  },
  {
    connector: adaptConnector(teamGamificationConnector, {
      id: "team-gamification",
      name: "Team Gamification",
      description: "Cross-org leaderboards + team challenges",
      phase: 4,
      subscribedDomains: ["gamification", "team"] as EventDomain[],
      publishedDomains: ["gamification", "team"] as EventDomain[],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(databaseChangeConnector, {
      id: "database-change",
      name: "Database Change Bridge",
      description: "Row-change events from Postgres triggers → Mycelium backbone (Realtime ↔ Mycelium Phase 1)",
      phase: 6,
      subscribedDomains: ["task", "notification", "crm", "accounting", "ai", "system"] as EventDomain[],
      publishedDomains: [],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(auditTrailConnector, {
      id: "audit-trail",
      name: "Audit Trail",
      description: "Centralized event logging across all domains (must be last non-PGMQ connector)",
      phase: 4,
      subscribedDomains: ["task", "sales", "calendar", "notification", "gamification", "accounting", "integration", "user", "team", "workload", "ai", "system", "crm", "document"] as EventDomain[],
      publishedDomains: [],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(consoleLogConnector, {
      id: "orchestration-console-log",
      name: "Orchestration Console Log",
      description: "Mirrors allow-listed events into cortex_events_log for the in-app Console drawer",
      phase: 4,
      subscribedDomains: ["ai", "task", "notification", "gamification", "accounting", "system"] as EventDomain[],
      publishedDomains: [],
      requiresPgmq: false,
    }),
  },
  {
    connector: adaptConnector(webhookBridgeConnector, {
      id: "webhook-bridge",
      name: "Webhook Bridge",
      description: "PGMQ-native webhook delivery (eliminates HTTP polling)",
      phase: 5,
      subscribedDomains: ["task", "sales", "calendar", "accounting", "notification", "gamification", "crm", "document"] as EventDomain[],
      publishedDomains: ["webhook"] as EventDomain[],
      requiresPgmq: true,
      executionSurface: "both",
    }),
  },
  {
    connector: adaptConnector(cortexTriggerConnector, {
      id: "cortex-trigger",
      name: "Cortex Trigger",
      description: "Business events → autonomous Cortex work queue (cortex_tasks)",
      phase: 5,
      subscribedDomains: ["task", "sales", "accounting", "system", "user"] as EventDomain[],
      publishedDomains: ["cortex"] as EventDomain[],
      requiresPgmq: true,
      executionSurface: "both",
    }),
  },
  {
    connector: messagingConnector,
  },
  {
    connector: adaptConnector(memoryConsolidatorConnector, {
      id: "memory-consolidator",
      name: "Memory Consolidator",
      description: "Synthesizes child-scope unified_memory rows into parent-scope summaries (federated rollup)",
      phase: 5,
      subscribedDomains: ["memory"] as EventDomain[],
      publishedDomains: [],
      requiresPgmq: false,
    }),
  },
];

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize the mycelium protocol network
 * Call this during application startup
 */
export async function initializeMyceliumProtocol(): Promise<void> {
  logger.info("🍄 Initializing Mycelium Protocol - interconnecting services");

  try {
    // Step 1: Register Event Backbone with Service Registry
    serviceRegistry.register(
      "myceliumEventBackbone",
      () => myceliumEventBackbone,
      {
        features: {
          caching: false,
          monitoring: true,
          rateLimiting: false,
          logging: true,
          metrics: true,
        },
        limits: {
          maxRetries: 3,
          timeout: 5000,
        },
      }
    );

    // Step 2: Initialize Event Backbone
    await myceliumEventBackbone.initialize();

    // Step 3: Register all connectors with the registry
    for (const { connector } of CONNECTOR_DEFINITIONS) {
      connectorRegistry.register(connector);
    }

    // Server-only connectors loaded dynamically to prevent node:crypto leak to browser
    if (typeof window === "undefined") {
      const { boxOrchestrationConnector } = await import("./box-orchestration.connector");
      connectorRegistry.register(boxOrchestrationConnector);
    }

    // Step 4: Initialize all connectors (transport-aware, ordered)
    await connectorRegistry.initializeAll();

    // Step 5: Forms connector subscribes to backbone for submission → action dispatch
    await initializeForms();

    const stats = myceliumEventBackbone.getStats();
    const registryStats = connectorRegistry.getStats();

    logger.info("✅ Mycelium Protocol initialized", {
      subscribers: stats.subscriberCount,
      enrichers: stats.enricherCount,
      connectorsActive: registryStats.initialized,
      connectorsSkipped: registryStats.skipped,
      auditTrail: "active",
      message: "Services are now interconnected through mycelium event backbone",
    });

    // Log active connectors for observability
    logger.info("🧠 Mycelium Protocol Status", {
      activeConnectors: registryStats.connectors
        .filter((c) => c.status === "active")
        .map((c) => `${c.name}: ${c.subscribedDomains.join("/")} → ${c.publishedDomains.join("/") || "(observer)"}`),
      skippedConnectors: registryStats.connectors
        .filter((c) => c.status === "skipped")
        .map((c) => c.name),
      latentPotential: [
        "Electric SQL Offline Intelligence",
      ],
      message: "Additional connectors can be added via connectorRegistry.register()",
    });

    // Report initial health via PGMQ if transport is active
    if (getMyceliumTransportType() === "pgmq") {
      pgmqHealthMonitor
        .reportHealth({
          serviceName: "mycelium-protocol",
          status: "healthy",
          serviceType: "connector",
          domains: ["system"],
          details: {
            subscribers: stats.subscriberCount,
            enrichers: stats.enricherCount,
            activeConnectors: registryStats.initialized,
          },
        })
        .catch((err) => {
          // Non-critical — don't block startup, but log for debugging
          logger.debug("Initial health report failed (non-critical)", {
            error: err instanceof Error ? err.message : String(err),
          });
        });
    }
  } catch (error) {
    logger.error("❌ Failed to initialize Mycelium Protocol", {
      error: error instanceof Error ? error.message : "Unknown error",
    });
    throw error;
  }
}

/**
 * Dispose the mycelium protocol network
 * Call this during application shutdown for graceful cleanup
 * ConnectorRegistry handles reverse-order disposal automatically.
 */
export async function disposeMyceliumProtocol(): Promise<void> {
  logger.info("🍄 Disposing Mycelium Protocol - disconnecting services");

  try {
    await disposeForms();

    // Dispose all connectors via registry (reverse initialization order)
    await connectorRegistry.disposeAll();

    // Dispose backbone last
    await myceliumEventBackbone.dispose();

    logger.info("✅ Mycelium Protocol disposed successfully");
  } catch (error) {
    logger.error("⚠️ Error disposing Mycelium Protocol", {
      error: error instanceof Error ? error.message : "Unknown error",
    });
    // Continue even if disposal fails - don't block shutdown
  }
}

/**
 * Get mycelium protocol health status
 */
export function getMyceliumHealth(): {
  status: "healthy" | "degraded" | "down";
  stats: ReturnType<typeof myceliumEventBackbone.getStats>;
  message: string;
} {
  try {
    const stats = myceliumEventBackbone.getStats();

    // Check for issues
    const errorRate = stats.totalEventsProcessed > 0
      ? stats.errorCount / stats.totalEventsProcessed
      : 0;

    let status: "healthy" | "degraded" | "down" = "healthy";
    let message = "Mycelium protocol operating normally";

    if (errorRate > 0.1) {
      status = "degraded";
      message = `High error rate: ${(errorRate * 100).toFixed(1)}%`;
    } else if (stats.subscriberCount === 0) {
      status = "degraded";
      message = "No active subscribers - services not interconnected";
    } else if (stats.eventsInFlight > 1000) {
      status = "degraded";
      message = "High event backlog - processing may be delayed";
    }

    return { status, stats, message };
  } catch (error) {
    return {
      status: "down",
      stats: {
        totalEventsPublished: 0,
        totalEventsProcessed: 0,
        eventsInFlight: 0,
        subscriberCount: 0,
        enricherCount: 0,
        eventsByDomain: {} as Record<EventDomain, number>,
        averageProcessingTime: 0,
        errorCount: 0,
        deadLetterCount: 0,
        circuitBreakerTrips: 0,
        rateLimitDrops: 0,
      },
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
